import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { keyframes } from '@emotion/react';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import useResponsiveValue from '../../../../hooks/use-responsive-value.esm.js';
import { useSafeThemeUI } from '../../../../theme-provider/use-theme-ui.esm.js';
import { get } from '../../../../utils/object.esm.js';
import { resolveSize } from '../../../../utils/size-utils.esm.js';
import { BoxPrivate } from '../../box/box.esm.js';
import Circle from '../../svg/circle.esm.js';
import Svg from '../../svg/svg.esm.js';

var _excluded = ["className", "size", "strokeWidth", "title", "color", "animationDuration"];
var animLinearRotate = keyframes({
  '0%': {
    transform: 'rotate(0deg)'
  },
  '100%': {
    transform: 'rotate(360deg)'
  }
});
var pi = 3.14159265;
var strokeDashoffset05 = (1 - 0.05) * pi * 2 * 45 + "px";
var strokeDashoffset8 = (1 - 0.8) * pi * 2 * 45 + "px";
var animStrokeRotate = keyframes({
  '0%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(0)'
  },
  '12.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(0)'
  },
  '12.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(72.5deg)'
  },
  '25%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(72.5deg)'
  },
  '25.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(270deg)'
  },
  '37.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(270deg)'
  },
  '37.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(161.5deg)'
  },
  '50%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(161.5deg)'
  },
  '50.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(180deg)'
  },
  '62.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(180deg)'
  },
  '62.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(251.5deg)'
  },
  '75%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(251.5deg)'
  },
  '75.0001%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotate(90deg)'
  },
  '87.5%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotate(90deg)'
  },
  '87.5001%': {
    strokeDashoffset: strokeDashoffset8,
    transform: 'rotateX(180deg) rotate(341.5deg)'
  },
  '100%': {
    strokeDashoffset: strokeDashoffset05,
    transform: 'rotateX(180deg) rotate(341.5deg)'
  }
});
var wrapperCss = {
  position: 'relative',
  overflow: 'hidden'
};
var LoadingSpinner = /*#__PURE__*/React.forwardRef(function LoadingSpinner(props, ref) {
  var theme = useSafeThemeUI(props);
  var className = props.className,
    _props$size = props.size,
    size = _props$size === void 0 ? 'md' : _props$size,
    _props$strokeWidth = props.strokeWidth,
    strokeWidth = _props$strokeWidth === void 0 ? 4 : _props$strokeWidth,
    _props$title = props.title,
    title = _props$title === void 0 ? 'Loading...' : _props$title,
    _props$color = props.color,
    color = _props$color === void 0 ? 'primary' : _props$color,
    _props$animationDurat = props.animationDuration,
    animationDuration = _props$animationDurat === void 0 ? 2000 : _props$animationDurat,
    rest = _objectWithoutProperties(props, _excluded);
  var circleAnimationDuration = animationDuration * 2 + 'ms';
  var spinnerSize = resolveSize(size, get(theme, 'sizes.spinner'));
  var _size = Array.isArray(spinnerSize) ? useResponsiveValue(spinnerSize) : spinnerSize;
  var svgCss = useMemo(function () {
    return {
      transform: 'rotate(-90deg)',
      transformOrigin: 'center',
      overflow: 'visible',
      animationName: animLinearRotate.toString(),
      animationDuration: animationDuration + 'ms',
      // 2000
      animationTimingFunction: 'linear',
      animationIterationCount: 'infinite',
      padding: '1px'
    };
  }, [animationDuration]);
  var circleCss = useMemo(function () {
    return {
      fill: 'transparent',
      transformOrigin: 'center',
      transition: 'stroke-dashoffset 225ms linear',
      transitionProperty: 'stroke',
      animationName: animStrokeRotate.toString(),
      animationDuration: circleAnimationDuration,
      // 4000
      animationTimingFunction: 'cubic-bezier(0.35, 0, 0.25, 1)',
      animationIterationCount: 'infinite',
      strokeDasharray: '282.743px',
      strokeWidth: '10%'
    };
  }, [circleAnimationDuration]);
  return /*#__PURE__*/React.createElement(BoxPrivate, _extends({
    color: color,
    className: classNames({
      'csg-tui-progress-spinner': true
    }, className),
    ref: ref,
    width: _size,
    height: _size,
    role: "progressbar",
    __css: wrapperCss
  }, rest), /*#__PURE__*/React.createElement(Svg, {
    id: "loading_spinner",
    viewBox: "0 0 100 100",
    preserveAspectRatio: "xMidYMid meet",
    strokeWidth: strokeWidth,
    role: "img",
    __css: svgCss
  }, /*#__PURE__*/React.createElement("title", null, title), /*#__PURE__*/React.createElement(Circle, {
    cx: "50%",
    cy: "50%",
    r: "45",
    __css: circleCss
  })));
});

export default LoadingSpinner;
//# sourceMappingURL=loading-spinner.esm.js.map
